import {
    Badge,
    Breadcrumb,
    Button,
    Col,
    Container,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
    Pagination, Toast, InputGroup
} from "react-bootstrap";
import {Suspense, useCallback, useContext, useEffect, useRef, useState} from "react";
import {BackendContext} from "../../context/Backend";
import styles from "./usermanagement.module.css";
import {Await, useActionData, useLoaderData, useLocation, useNavigate, useRouteLoaderData} from "react-router";
import {useTranslation} from "react-i18next";
import {useSubmit} from "react-router-dom";
import {CheckCircle, Search, X} from "react-bootstrap-icons";
import debounce from "lodash.debounce"
import trashSvg from "../../icons/trash.svg"
import {LIMIT} from "../../constants";

const Usermanagement = (props) =>{

    const backend = useContext(BackendContext);

    const [userToDelete, setUserToDelete] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState(null);
    const {state} = useLocation();
    const submit = useSubmit();
    const [showToast, setShowToast] = useState(!!state?.message);
    const navigate = useNavigate();
    const { usermanagement, currentUser } = useRouteLoaderData("root");
    const searchInput = useRef();
    const users = useState();

    const {t} = useTranslation();

    const handleActiveSwitch = (e) =>{
        e.stopPropagation();
        let userId = e.currentTarget.value;
        let active = e.currentTarget.checked ? 1 : 0;
        backend.api.setUserActive({userId: userId, active: active},
            (res) => {
                handlePage(page);
            },
            (error) => {
                navigate("/error", backend.api.getErrorState(error));
            });
    }

    const handleEdit = (user) => {
        navigate("/edit/" + user.userId);
    };

    const handleNew = (e) => {
        navigate("/new");
    }

    const handleInvite = (e) => {
        navigate("/invite");
    }

    const handleCloseModal = (e) => {
        setUserToDelete(null);
    };

    const handleMultiEdit = (e) => {
        navigate("/multiedit",{state:{data:selectedUsers}});
    }

    const handleCheckbox = (e) => {
        e.stopPropagation();
        const userId = e.currentTarget.getAttribute("data-user-id");
        if (e.currentTarget.checked) {
            setSelectedUsers((prev) => new Set(prev.add(userId)));
        } else {
            setSelectedUsers( (prev) => {
                prev.delete(userId);
                return new Set(prev);
            });
        }
    }

    const handleDeleteUser = (e) => {
        backend.api.deleteUser({userId:userToDelete.userId},(resp)=>{
            setUserToDelete(null);
            handlePage(page);
        },(error)=>{
            navigate("/error", backend.api.getErrorState(error));
        })
    }

    const handlePage = (page) => {
        setPage(page);
        navigate("/?page=" + page + (search != null ? "&search=" + encodeURIComponent(search):''));
    }

    const handleSearch = useRef(
        debounce((e) => {
            setSearch(e.target.value);
        }, 300)).current;

    const handleClear = (e) => {
        setSearch("");
        searchInput.current.value = "";
    }

    useEffect(()=>{
        if (search != null) {
            navigate("/?search=" + encodeURIComponent(search),{replace:false});
        }
    },[search])

    if (!currentUser?.roles?.some(r => r.userManagement)) return null;

    return (
        <Container fluid={true}>
            <Row className={"justify-content-end"}>
                <Col className={"d-flex justify-content-start"}>
                    <Breadcrumb className={"mt-3"}>
                        <Breadcrumb.Item active>{t('breadcrumbs.home')}</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
                <Col className={"d-flex justify-content-end"}>
                    <div className={styles.credits}>
                        <h2 className={"text-uppercase"}>
                            {t('credits.header')} <Badge bg="secondary">{currentUser.generalInfo.credits}</Badge>
                        </h2>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col className={"justify-content-center d-flex"}>
                    <Form>
                        <InputGroup className={styles.searchbar + " rounded-2 bg-primary"}>
                            <Search size={"2rem"} color={"white"} className={"p-2 m-1 align-bottom"}/>
                            <Form.Control
                                type="text"
                                onChange={handleSearch}
                                ref={searchInput}
                            />
                            <X size={"2rem"} color={"white"} className={"p-2 m-1 align-bottom"} onClick={handleClear}/>
                        </InputGroup>
                    </Form>
                </Col>
            </Row>
            <Row>
                <Col className={"d-flex justify-content-center"}>
                    <Toast className={"shadow-none"} onClose={() => setShowToast(false)}
                           delay={3000} show={showToast} bg={"success"} autohide>
                        <Toast.Body className={"success py-0 px-2"}><CheckCircle className={"m-1 align-bottom"}/>{state?.message}</Toast.Body>
                    </Toast>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form>
                        <Table responsive hover={true}>
                            <thead>
                            <tr className={"text-uppercase"}>
                                <th></th>
                                <th className={styles.label}>{t('usermanagement.tableActive')}</th>
                                <th className={styles.label}>{t('usermanagement.tableLogin')}</th>
                                <th className={styles.label}>{t('usermanagement.tableName')}</th>
                                <th className={styles.label}>{t('usermanagement.tableCredits')}</th>
                                <th className={styles.label}></th>
                            </tr>
                            </thead>
                            <tbody>
                            <Suspense fallback={<tr>
                                <td colSpan={6} className={"border-bottom-0"}>
                                    <Container className={"d-flex border-0 justify-content-center"}>
                                        <Spinner
                                            className={"me-2 d-flex"}
                                            animation={"border"}
                                            size="lg"
                                        />
                                    </Container>
                                </td>
                            </tr>}>
                                <Await resolve={usermanagement}>
                                    {(usermanagement) => {
                                        let items = [];
                                        for (let number = 1; number <= Math.ceil(usermanagement.count / LIMIT); number++) {
                                            items.push(
                                                <Pagination.Item key={number} onClick={() => handlePage(number)}
                                                                 active={number === page}>
                                                    {number}
                                                </Pagination.Item>,
                                            );
                                        }
                                        let ret = usermanagement.users.map((user) => {
                                            return user.isSuperuser ? "" :
                                                <tr key={user.userId}>
                                                    <td><Form.Check
                                                        role={"button"}
                                                        type="checkbox"
                                                        data-user-id={user.userId}
                                                        onClick={handleCheckbox}
                                                    /></td>
                                                    <td><Form.Switch
                                                        role={"button"}
                                                        type="switch"
                                                        id={"switch-" + user.userId}
                                                        onChange={handleActiveSwitch}
                                                        value={user.userId}
                                                        checked={user.active}
                                                    /></td>
                                                    <td className={styles.cell} role={"button"} onClick={() => handleEdit(user)}>{user.login}</td>
                                                    <td className={styles.cell} role={"button"} onClick={() => handleEdit(user)}>{user.firstName + " " + user.lastName}</td>
                                                    <td className={styles.cell} role={"button"} onClick={() => handleEdit(user)}>{user.credits}</td>
                                                    <td>
                                                        <img role={"button"} src={trashSvg}
                                                                onClick={
                                                                    (e) => {
                                                                        e.stopPropagation();
                                                                        setUserToDelete(user)
                                                                    }
                                                                }
                                                                size="sm" className={"p-1 rounded-1 bg-primary"}/></td>
                                                </tr>;
                                        })
                                        ret.push(<tr key={"xx"} className={"paging"}>
                                            <td colSpan={6} className={"border-bottom-0"}><Pagination
                                                size={"sm"}>{items}</Pagination></td>
                                        </tr>);
                                        return ret;
                                    }}
                                </Await>
                            </Suspense>
                            </tbody>
                        </Table>
                    </Form>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="d-flex gap-2 p-2 justify-content-center">
                        <Button className={"usermanagment-button websobutton"} variant="primary" onClick={handleNew} size="md">
                            {t('system.newUserButton')}
                        </Button>
                        <Button className={"usermanagment-button websobutton"} variant="primary" onClick={handleInvite} size="md">
                            {t('system.inviteUserButton')}
                        </Button>
                        <Button className={"usermanagment-button websobutton"} variant="primary" disabled={selectedUsers.size > 1 ? (false) : (true)}
                                onClick={handleMultiEdit} size="md">
                            {t('system.editMultiButton')}
                        </Button>
                    </div>
                </Col>
            </Row>
            <Modal
                show={userToDelete != null ? (true) : (false)}
                onHide={handleCloseModal}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title className={"text-danger"}>{t('system.deleteUserModalHeader')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t('system.deleteUserModalContent')} {userToDelete?.firstName} {userToDelete?.lastName}?
                </Modal.Body>
                <Modal.Footer>
                    <Button className={"websobutton"} onClick={handleCloseModal}>
                        {t('system.noButton')}
                    </Button>
                    <Button className={"websobutton"} variant="danger" onClick={handleDeleteUser}>{t('system.yesButton')}</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default Usermanagement;